import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Bookmark, BookmarkCheck, Plus, Loader2, LogIn, X } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";
import { trackEvent } from "@/lib/ga4";

/**
 * Button to add an episode to one of the logged-in user's collections.
 * - Logged out: shows a subtle "Entre para incluir em coleção" pill linking to /conta.
 * - Logged in: opens a picker with existing collections + "Nova coleção…".
 *
 * Visual variant:
 *   "full"    → full pill button (for EpisodePage)
 *   "compact" → small icon button (for EpisodeCard overlay)
 */
export default function AddToCollectionButton({ episode, variant = "full" }) {
  const { user, collections } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!open || !user) return;
    setLoading(true);
    collections.list()
      .then((list) => setItems(list))
      .catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [open, user]); // eslint-disable-line

  const stop = (e) => { e.stopPropagation(); e.preventDefault(); };

  if (!user) {
    if (variant === "compact") {
      return (
        <Link
          to="/conta"
          state={{ mode: "register" }}
          onClick={stop}
          title="Entre para incluir em uma coleção"
          aria-label="Entrar para adicionar este episódio a uma coleção"
          className="absolute top-2 right-2 z-20 w-8 h-8 rounded-full bg-slate-950/70 backdrop-blur-sm border border-slate-700/60 flex items-center justify-center text-slate-300 opacity-0 group-hover:opacity-100 transition hover:border-sky-500/60 hover:text-sky-300"
          data-testid={`addtocol-compact-login-${episode.id}`}
        >
          <LogIn className="w-3.5 h-3.5" />
        </Link>
      );
    }
    return (
      <Link
        to="/conta"
        state={{ mode: "register" }}
        className="btn-ghost !opacity-80 hover:!opacity-100"
        data-testid={`addtocol-login-${episode.id}`}
      >
        <Bookmark className="w-4 h-4 text-sky-400" />
        Entrar para incluir em uma coleção
      </Link>
    );
  }

  const addTo = async (c) => {
    setBusyId(c.id);
    try {
      const res = await collections.addEpisode(c.id, episode.id);
      toast.success(res.already_in ? "Já estava nessa coleção" : `Adicionado a "${c.name}"`);
      if (!res.already_in) {
        trackEvent("add_to_collection", {
          episode_id: episode.id,
          episode_title: episode.title,
          podcast_slug: episode.podcast_slug,
          collection_id: c.id,
          new_collection: false,
        });
      }
      setItems((list) => list.map((x) => x.id === c.id ? { ...x, episode_ids: res.episode_ids } : x));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusyId(null); }
  };

  const createAndAdd = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const c = await collections.create({ name, episode_ids: [episode.id] });
      toast.success(`Coleção criada e episódio adicionado a "${c.name}"`);
      trackEvent("add_to_collection", {
        episode_id: episode.id,
        episode_title: episode.title,
        podcast_slug: episode.podcast_slug,
        collection_id: c.id,
        new_collection: true,
      });
      setNewName("");
      setItems((list) => [c, ...list]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setCreating(false); }
  };

  const trigger = variant === "compact" ? (
    <button
      onClick={(e) => { stop(e); setOpen(true); }}
      title="Adicionar a uma coleção"
      aria-label="Adicionar este episódio a uma coleção"
      className="absolute top-2 right-2 z-20 w-8 h-8 rounded-full bg-slate-950/70 backdrop-blur-sm border border-slate-700/60 flex items-center justify-center text-sky-400 opacity-0 group-hover:opacity-100 transition hover:border-sky-500/60"
      data-testid={`addtocol-open-${episode.id}`}
    >
      <Bookmark className="w-3.5 h-3.5" />
    </button>
  ) : (
    <button
      onClick={(e) => { stop(e); setOpen(true); }}
      className="btn-ghost"
      data-testid={`addtocol-open-${episode.id}`}
    >
      <Bookmark className="w-4 h-4 text-sky-400" />
      Incluir em uma coleção
    </button>
  );

  return (
    <>
      {trigger}
      {open && (
        <div
          className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
          onClick={() => setOpen(false)}
          data-testid={`addtocol-modal-${episode.id}`}
        >
          <div
            className="w-full max-w-md my-16 bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <span className="text-[11px] uppercase tracking-widest text-sky-400 font-semibold">Adicionar a</span>
                <h3 className="font-serif text-xl font-bold text-white leading-tight mt-0.5">Uma coleção sua</h3>
                <p className="text-xs text-slate-400 mt-1 line-clamp-2">{episode.title}</p>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white" data-testid="addtocol-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {loading ? (
              <div className="text-slate-400 text-sm">Carregando…</div>
            ) : (
              <ul className="space-y-1 max-h-[40vh] overflow-y-auto pr-1">
                {items.length === 0 && (
                  <li className="text-sm text-slate-500 italic">Você ainda não tem coleções — crie a primeira abaixo.</li>
                )}
                {items.map((c) => {
                  const already = (c.episode_ids || []).includes(episode.id);
                  return (
                    <li key={c.id}>
                      <button
                        onClick={() => !already && addTo(c)}
                        disabled={already || busyId === c.id}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition ${already ? "bg-emerald-500/10 text-emerald-300 cursor-default" : "text-slate-200 hover:bg-slate-800/70 hover:text-white"}`}
                        data-testid={`addtocol-pick-${c.id}`}
                      >
                        {busyId === c.id ? (
                          <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                        ) : already ? (
                          <BookmarkCheck className="w-4 h-4 text-emerald-400 shrink-0" />
                        ) : (
                          <Bookmark className="w-4 h-4 text-sky-400 shrink-0" />
                        )}
                        <span className="flex-1 truncate">{c.name}</span>
                        <span className="text-[10px] text-slate-500">{(c.episode_ids || []).length} ep.</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            <form onSubmit={createAndAdd} className="pt-3 border-t border-slate-800 flex gap-2">
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Nova coleção (ex: Aula 05/03)"
                className="flex-1 bg-slate-950 border border-slate-800 rounded-full px-4 py-2 text-sm text-white placeholder:text-slate-500 focus:border-sky-500/60 outline-none"
                data-testid="addtocol-new-name"
              />
              <button type="submit" disabled={creating || !newName.trim()} className="btn-primary !py-2 !text-xs disabled:opacity-50" data-testid="addtocol-new-submit">
                {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                Criar
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
